import { ChevronRight, Gift, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { Dialog } from "../../components/ui/Dialog";
import { EmptyState, Skeleton } from "../../components/ui/Feedback";
import { Input } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";
import { Hint, LabelWithHint } from "../../components/ui/Tooltip";
import { cn } from "../../lib/cn";
import { majorToMinor, minorToMajor } from "../../lib/money";
import type { CampaignGift, TierWithGifts } from "../../lib/types";
import { GiftEditor } from "./GiftEditor";
import { TierFallbackField } from "./TierFallbackField";
import {
	useCreateGift,
	useCreateTier,
	useDeleteGift,
	useDeleteTier,
	useTiers,
	useUpdateGift,
	useUpdateTier,
} from "./useTiers";

interface GiftDraft {
	tierId: string;
	giftId: string | null;
	title: string;
}

const emptyGift = (tierId: string): GiftDraft => ({ tierId, giftId: null, title: "" });

const draftFromGift = (gift: CampaignGift): GiftDraft => ({
	tierId: gift.tierId,
	giftId: gift.id,
	title: gift.title,
});

const TierCard = ({
	tier,
	campaignId,
	index,
	singleTier,
	onAddGift,
	onEditGift,
}: {
	tier: TierWithGifts;
	campaignId: string;
	index: number;
	singleTier: boolean;
	onAddGift: (tierId: string) => void;
	onEditGift: (gift: CampaignGift) => void;
}) => {
	const updateTier = useUpdateTier(campaignId);
	const deleteTier = useDeleteTier(campaignId);
	const deleteGift = useDeleteGift(campaignId);
	const toast = useToast();
	const confirm = useConfirm();
	const [threshold, setThreshold] = useState(minorToMajor(tier.thresholdMinor));
	const [expanded, setExpanded] = useState<string | null>(null);

	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };

	const commitThreshold = () => {
		const thresholdMinor = majorToMinor(threshold || "0");
		if (thresholdMinor === tier.thresholdMinor) return;
		updateTier.mutate(
			{ tierId: tier.id, thresholdMinor },
			{ onSuccess: () => toast.push("Threshold updated"), ...notify },
		);
	};

	return (
		<div className="rounded-md border border-line">
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
				<div className="flex items-center gap-2">
					<span className="text-xs font-medium text-ink">
						{singleTier ? "The gift" : `Tier ${index + 1}`}
					</span>
					<span className="text-xs text-ink-muted">
						{singleTier ? "minimum spend" : "unlocks at"}
					</span>
					<Input
						type="number"
						min="0"
						step="0.01"
						value={threshold}
						onChange={(event) => setThreshold(event.target.value)}
						onBlur={commitThreshold}
						className="h-8 w-28"
						aria-label={singleTier ? "Minimum spend" : "Tier threshold"}
					/>
					<Hint label="About the threshold">
						{singleTier
							? "The cart subtotal, after discounts and excluding gifts, a shopper must reach before this gift is offered. Leave it at zero to offer it on every order."
							: "The cart subtotal, after discounts and excluding gifts, that a shopper must reach to unlock everything in this tier."}
					</Hint>
				</div>
				<div className="flex items-center gap-2">
					<Button variant="outline" size="sm" onClick={() => onAddGift(tier.id)}>
						<Plus className="h-3.5 w-3.5" strokeWidth={2} />
						Add gift
					</Button>
					<Button
						variant="ghost"
						size="sm"
						aria-label="Delete tier"
						disabled={singleTier}
						onClick={() =>
							confirm({
								title: "Delete this tier?",
								description: `Tier ${index + 1} and its ${tier.gifts.length} gift(s) will be removed.`,
								onConfirm: () =>
									deleteTier.mutate(tier.id, {
										onSuccess: () => toast.push("Tier deleted"),
										...notify,
									}),
							})
						}
					>
						<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
					</Button>
				</div>
			</div>

			{tier.gifts.length === 0 ? (
				<p className="px-4 py-5 text-center text-xs text-ink-muted">
					No gifts yet. A tier can hold several gifts, which is how a shopper receives two items at
					one threshold.
				</p>
			) : (
				<ul className="divide-y divide-line">
					{tier.gifts.map((gift) => {
						const isOpen = expanded === gift.id;
						return (
							<li key={gift.id}>
								<div className="flex items-center justify-between gap-4 px-4 py-3">
									<button
										type="button"
										onClick={() => setExpanded(isOpen ? null : gift.id)}
										className="flex min-w-0 flex-1 items-center gap-3 text-left"
									>
										<ChevronRight
											className={cn(
												"h-4 w-4 shrink-0 text-ink-faint transition-transform",
												isOpen && "rotate-90",
											)}
											strokeWidth={1.75}
										/>
										<Gift className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
										<span className="min-w-0">
											<span className="block truncate text-sm font-medium text-ink">
												{gift.title}
											</span>
											<span className="mt-0.5 block text-xs text-ink-faint">
												{gift.selection === "single"
													? "Shopper picks one"
													: "Everything inside is given"}
											</span>
										</span>
									</button>
									<div className="flex shrink-0 items-center gap-2">
										{gift.autoAdd ? <Badge variant="quiet">Added for them</Badge> : null}
										<Button variant="ghost" size="sm" onClick={() => onEditGift(gift)}>
											Rename
										</Button>
										<Button
											variant="ghost"
											size="sm"
											aria-label={`Delete ${gift.title}`}
											onClick={() =>
												confirm({
													title: `Delete “${gift.title}”?`,
													description: "Its products go with it.",
													onConfirm: () =>
														deleteGift.mutate(gift.id, {
															onSuccess: () => toast.push("Gift deleted"),
															...notify,
														}),
												})
											}
										>
											<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
										</Button>
									</div>
								</div>
								{isOpen ? (
									<div className="border-t border-line bg-canvas px-4 py-4">
										<GiftEditor campaignId={campaignId} giftId={gift.id} />
									</div>
								) : null}
							</li>
						);
					})}
				</ul>
			)}
			{tier.gifts.length > 0 ? <TierFallbackField tier={tier} campaignId={campaignId} /> : null}
		</div>
	);
};

type TierEditorProps = {
	campaignId: string;
	singleTier: boolean;
};

export const TierEditor = ({ campaignId, singleTier }: TierEditorProps) => {
	const tiers = useTiers(campaignId);
	const createTier = useCreateTier(campaignId);
	const createGift = useCreateGift(campaignId);
	const updateGift = useUpdateGift(campaignId);
	const toast = useToast();
	const [draft, setDraft] = useState<GiftDraft | null>(null);

	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };

	const submitGift = () => {
		if (!draft || !draft.title.trim()) return;

		const payload = { title: draft.title.trim() };

		if (draft.giftId) {
			updateGift.mutate(
				{ giftId: draft.giftId, ...payload },
				{
					onSuccess: () => {
						toast.push("Gift saved");
						setDraft(null);
					},
					...notify,
				},
			);
			return;
		}

		createGift.mutate(
			{ tierId: draft.tierId, ...payload },
			{
				onSuccess: () => {
					toast.push("Gift added");
					setDraft(null);
				},
				...notify,
			},
		);
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-4">
				<p className="text-xs text-ink-muted">
					{singleTier
						? "One gift, handed over once the cart clears the minimum spend. Leave the minimum at zero to give it away on any order."
						: "A tier is a spend threshold. Put two gifts in one tier to hand over two items at once."}
				</p>
				{singleTier ? null : (
					<Button
						variant="outline"
						size="sm"
						disabled={createTier.isPending}
						onClick={() =>
							createTier.mutate(0, { onSuccess: () => toast.push("Tier added"), ...notify })
						}
					>
						<Plus className="h-3.5 w-3.5" strokeWidth={2} />
						Add tier
					</Button>
				)}
			</div>

			{tiers.isPending ? <Skeleton className="h-32 w-full" /> : null}

			{tiers.data && tiers.data.items.length === 0 ? (
				<EmptyState
					title={singleTier ? "No gift yet" : "No tiers yet"}
					description={
						singleTier
							? "Reload the page. A simple campaign is created with its single tier ready."
							: "Add a tier to set the first spend threshold."
					}
				/>
			) : null}

			{tiers.data?.items.map((tier, index) => (
				<TierCard
					key={tier.id}
					tier={tier}
					index={index}
					singleTier={singleTier}
					campaignId={campaignId}
					onAddGift={(tierId) => setDraft(emptyGift(tierId))}
					onEditGift={(gift) => setDraft(draftFromGift(gift))}
				/>
			))}

			<Dialog
				open={draft !== null}
				onOpenChange={(open) => !open && setDraft(null)}
				title={draft?.giftId ? "Rename gift" : "Add gift"}
				description="Once it exists you choose the products that go in it."
				footer={
					<>
						<Button variant="outline" onClick={() => setDraft(null)}>
							Cancel
						</Button>
						<Button
							onClick={submitGift}
							disabled={!draft?.title.trim() || createGift.isPending || updateGift.isPending}
						>
							{createGift.isPending || updateGift.isPending
								? "Saving…"
								: draft?.giftId
									? "Save gift"
									: "Add gift"}
						</Button>
					</>
				}
			>
				{draft ? (
					<div className="space-y-4">
						<label className="block space-y-1.5">
							<LabelWithHint label="Name">
								What the shopper sees this gift called, like “Reversible bucket hat”.
							</LabelWithHint>
							<Input
								autoFocus
								value={draft.title}
								placeholder="Reversible bucket hat"
								onChange={(event) => setDraft({ ...draft, title: event.target.value })}
							/>
						</label>
					</div>
				) : null}
			</Dialog>
		</div>
	);
};
